/**
 * Shareable practice plan progress report – snapshot encoding for URL.
 * Report page uses noindex, nofollow; only people with the link can view.
 */

import type { PlanItem } from "./planTypes";
import { getLessonPlan } from "./lessonPlan";
import { generateId } from "./practicePlan";
import type { RepertoirePiece } from "./repertoire.types";
import { supabase } from "./supabaseClient";
import { nanoid } from "nanoid";
import { resetMarkdownTodos } from "./richText";

export interface ReportSnapshotItem {
  id?: string;
  text: string;
  checked: boolean;
  checkedDate?: string;
  blockType?: string;
  children: ReportSnapshotItem[];
  segmentGoal?: string;
  allocatedTime?: number;
  allocationPeriod?: 'day' | 'week';
  repertoirePieceId?: string;
  videoUrl?: string;
}

export interface ReportLogEntry {
  itemId: string;
  itemName: string;
  seconds: number;
  completionsCount?: number;
}

export interface ReportLogSummary {
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  totalSeconds: number;
  pieces: ReportLogEntry[];
}

export interface ReportSnapshot {
  v: number;
  date: string; // ISO
  title?: string;
  items: ReportSnapshotItem[];
  lessonPlanItems?: ReportSnapshotItem[];
  logSummary?: ReportLogSummary;
  lastWeekLogSummary?: ReportLogSummary;
  embeddedPieces?: Record<string, RepertoirePiece>;
  creatorName?: string;
}

function itemToSnapshot(item: PlanItem): ReportSnapshotItem {
  return {
    text: item.text,
    checked: item.checked ?? false,
    checkedDate: item.checkedDate,
    blockType: item.blockType,
    children: item.children ? item.children.map(itemToSnapshot) : [],
    repertoirePieceId: item.repertoirePieceId,
    ...(item.blockType === 'segment' ? {
      id: item.id,
      segmentGoal: item.segmentGoal,
      allocatedTime: item.allocatedTime,
      allocationPeriod: item.allocationPeriod,
      videoUrl: item.videoUrl,
    } : {}),
  };
}

export function createReportSnapshot(
  items: PlanItem[],
  title?: string,
  logSummary?: ReportLogSummary,
  repertoirePieces?: RepertoirePiece[],
  creatorName?: string,
  lessonItems?: PlanItem[],
  lastWeekLogSummary?: ReportLogSummary
): ReportSnapshot {
  const effectiveLessonItems = lessonItems ?? getLessonPlan();
  const snapshot: ReportSnapshot = {
    v: 1,
    date: new Date().toISOString(),
    title: title ?? "Practice Plan & Progress Report",
    items: items.map(itemToSnapshot),
    lessonPlanItems: effectiveLessonItems.map(itemToSnapshot),
    logSummary,
    lastWeekLogSummary,
    creatorName,
  };

  if (repertoirePieces && repertoirePieces.length > 0) {
    const embeddedPieceIds = new Set<string>();
    const collectPieceIds = (item: PlanItem | ReportSnapshotItem) => {
      if (item.repertoirePieceId) {
        embeddedPieceIds.add(item.repertoirePieceId);
      }
      item.children?.forEach(collectPieceIds);
    };
    items.forEach(collectPieceIds);
    effectiveLessonItems.forEach(collectPieceIds);

    const embeddedPieces: Record<string, RepertoirePiece> = {};
    repertoirePieces.forEach((piece) => {
      if (embeddedPieceIds.has(piece.id)) {
        embeddedPieces[piece.id] = piece;
      }
    });

    if (Object.keys(embeddedPieces).length > 0) {
      snapshot.embeddedPieces = embeddedPieces;
    }
  }

  return snapshot;
}

import { getPracticePlan } from "./practicePlan";
import { getThisWeekSummary, getLastWeekSummary } from "./practiceLog";
import { getSettings } from "./localStorage";

export const GLOBAL_PERMANENT_SHARE_ID_KEY = "practice-timer-share-id";
export const GLOBAL_LAST_PUBLISHED_DATE_KEY = "practice-timer-last-published-date";

export function getGlobalPermalinkId(): string | null {
  if (typeof window === "undefined") return null;
  return (
    localStorage.getItem(GLOBAL_PERMANENT_SHARE_ID_KEY) ||
    localStorage.getItem("practice-timer-lesson-share-id") ||
    null
  );
}

export function saveGlobalPermalinkId(id: string): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(GLOBAL_PERMANENT_SHARE_ID_KEY, id);
}

export function getGlobalLastPublishedDate(): string | null {
  if (typeof window === "undefined") return null;
  return (
    localStorage.getItem(GLOBAL_LAST_PUBLISHED_DATE_KEY) ||
    localStorage.getItem("practice-timer-lesson-last-published-date") ||
    null
  );
}

export function saveGlobalLastPublishedDate(dateIso: string): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(GLOBAL_LAST_PUBLISHED_DATE_KEY, dateIso);
}

export function createGlobalReportSnapshot(
  repertoirePieces?: RepertoirePiece[],
  creatorName?: string
): ReportSnapshot {
  const practiceItems = getPracticePlan();
  const lessonItems = getLessonPlan();
  const weekStartsOn = getSettings()?.weekStartsOn ?? 'monday';
  const logSummary = getThisWeekSummary(practiceItems, weekStartsOn);
  const lastWeekLogSummary = getLastWeekSummary(practiceItems, weekStartsOn);

  return createReportSnapshot(
    practiceItems,
    "Practice Plan & Progress Report",
    logSummary,
    repertoirePieces,
    creatorName,
    lessonItems,
    lastWeekLogSummary
  );
}

function base64UrlEncode(str: string): string {
  return btoa(unescape(encodeURIComponent(str)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64UrlDecode(str: string): string {
  const pad = str.length % 4;
  const base64 = (str.replace(/-/g, "+").replace(/_/g, "/") + "==".slice(0, pad ? 4 - pad : 0));
  return decodeURIComponent(escape(atob(base64)));
}


import LZString from "lz-string";

export function encodeReportToken(snapshot: ReportSnapshot): string {
  const json = JSON.stringify(snapshot);
  // Using compressToEncodedURIComponent for URL safety and compactness
  return LZString.compressToEncodedURIComponent(json);
}

export function decodeReportToken(token: string): ReportSnapshot | null {
  try {
    // Try LZ-String decompression first (new format)
    const decompressed = LZString.decompressFromEncodedURIComponent(token);
    if (decompressed) {
      const data = JSON.parse(decompressed) as ReportSnapshot;
      if (data?.v === 1 && Array.isArray(data?.items)) return data;
    }
  } catch {
    // ignore
  }

  // Fallback to old format (base64)
  try {
    const json = base64UrlDecode(token);
    const data = JSON.parse(json) as ReportSnapshot;
    if (data?.v === 1 && Array.isArray(data?.items)) return data;
  } catch {
    // invalid or corrupted
  }
  return null;
}

export async function shareReport(snapshot: ReportSnapshot, id?: string): Promise<string> {
  const finalId = id || nanoid(10);

  // If supabase is configured, save the report there
  if (supabase) {
    try {
      // Check if user is logged in
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id || null;

      const { error } = await supabase
        .from("shared_reports")
        .upsert({ id: finalId, user_id: userId, data: snapshot });

      if (!error) {
        // If shared anonymously, save the ID locally for later migration on login
        if (!userId && typeof window !== "undefined") {
          try {
            const localReports = JSON.parse(localStorage.getItem("local_shared_reports") || "[]");
            if (!localReports.includes(finalId)) {
              localReports.push(finalId);
              localStorage.setItem("local_shared_reports", JSON.stringify(localReports));
            }
          } catch (e) {
            console.warn("[shareReport] Failed to write to localStorage:", e);
          }
        }
        return getShortShareUrl(finalId);
      }
      console.warn("[shareReport] Supabase insert failed, trying fallback:", error);
    } catch (err) {
      console.warn("[shareReport] Supabase error, trying fallback:", err);
    }
  }

  // Fallback to Netlify Blobs in production if Supabase fails or is not configured
  if (!import.meta.env.DEV) {
    try {
      const response = await fetch("/.netlify/functions/share-report", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...snapshot, id: finalId }),
      });

      if (response.ok) {
        const data = await response.json();
        return getShortShareUrl(data.id);
      }

      // Log Blobs failure for debugging
      const errBody = await response.text();
      let errJson: { error?: string; detail?: string } = {};
      try {
        errJson = JSON.parse(errBody);
      } catch { }
      console.warn("[shareReport] Blobs failed:", response.status, errJson.detail || errBody);
    } catch (e) {
      console.warn("[shareReport] Fetch failed:", e);
    }
  }

  // Final fallback: generate a long token URL
  return getReportShareUrl(snapshot);
}

export function getShortShareUrl(id: string): string {
  return `${typeof window !== "undefined" ? window.location.origin : ""}/r/${id}`;
}

export function getReportShareUrl(snapshot: ReportSnapshot): string {
  // Legacy URL generation (still useful as fallback or for local dev without functions)
  const token = encodeReportToken(snapshot);
  const base = typeof window !== "undefined" ? window.location.origin : "";
  // In dev: use hash so token isn't in path (avoids "Could not proxy request" with long URLs)
  if (import.meta.env.DEV) {
    return `${base}/report#${token}`;
  }
  return `${base}/report/${token}`;
}


/**
 * After a user signs in, claim any reports that were created anonymously
 * while the user was not logged in. Those report IDs are stored in
 * localStorage under the key `local_shared_reports`.
 */
export async function claimAnonymousReports() {
  if (!supabase) return;
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData.session?.user?.id;
  if (!userId) return; // not logged in

  const local = JSON.parse(localStorage.getItem("local_shared_reports") ?? "[]");
  if (!Array.isArray(local) || local.length === 0) return;

  const { error } = await supabase
    .from("shared_reports")
    .update({ user_id: userId })
    .in("id", local);

  if (error) {
    console.warn("[claimAnonymousReports] Failed to associate reports:", error);
  } else {
    localStorage.removeItem("local_shared_reports");
  }
}

/**
 * Reconstruct a PracticePlanItem[] from a published ReportSnapshot.
 * Segment IDs are preserved (for log continuity); other items get fresh IDs.
 * All checked states are reset to false.
 */
export function restorePlanFromSnapshot(snapshot: ReportSnapshot): PlanItem[] {
  function snapshotItemToPlanItem(item: ReportSnapshotItem): PlanItem {
    return {
      id: item.id || generateId(),
      text: item.text ? resetMarkdownTodos(item.text) : item.text,
      checked: false,
      children: item.children ? item.children.map(snapshotItemToPlanItem) : [],
      isHeader: item.blockType === "heading1" || item.blockType === "heading2" || item.blockType === "heading3",
      blockType: item.blockType as PlanItem["blockType"],
      ...(item.allocatedTime != null ? { allocatedTime: item.allocatedTime } : {}),
      ...(item.allocationPeriod ? { allocationPeriod: item.allocationPeriod } : {}),
      ...(item.segmentGoal ? { segmentGoal: resetMarkdownTodos(item.segmentGoal) } : {}),
      ...(item.repertoirePieceId ? { repertoirePieceId: item.repertoirePieceId } : {}),
      ...(item.videoUrl ? { videoUrl: item.videoUrl } : {}),
    };
  }

  return snapshot.items.map(snapshotItemToPlanItem);
}

/**
 * Generates a clean, URL-safe anchor slug for headings in reports.
 * Strips markdown markup, removes non-alphanumeric chars, and collapses hyphens.
 */
export function slugifyHeader(text: string): string {
  if (!text) return "";
  const plain = text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // strip markdown links
    .replace(/\*\*(.+?)\*\*/g, "$1")         // strip bold
    .replace(/\*(.+?)\*/g, "$1")             // strip italic
    .replace(/^#+\s*/, "")                   // strip leading markdown heading markers
    .trim()
    .toLowerCase();

  return plain
    .replace(/[^\w\s-]/g, "") // remove non-word chars
    .replace(/\s+/g, "-")     // replace spaces with single hyphen
    .replace(/-+/g, "-")      // collapse duplicate hyphens
    .replace(/^-|-$/g, "");   // trim leading/trailing hyphens
}

/**
 * Checks if a target heading slug exists within a hierarchical list of report items.
 */
export function findSlugInItems(items: ReportSnapshotItem[] | undefined, targetSlug: string): boolean {
  if (!items || !items.length || !targetSlug) return false;
  const cleanTarget = targetSlug.toLowerCase().replace(/-/g, "");
  for (const item of items) {
    const isHeader =
      item.blockType === "heading1" ||
      item.blockType === "heading2" ||
      item.blockType === "heading3";
    if (isHeader) {
      const itemSlug = slugifyHeader(item.text);
      if (itemSlug === targetSlug || itemSlug.replace(/-/g, "") === cleanTarget) {
        return true;
      }
    }
    if (item.children && item.children.length > 0) {
      if (findSlugInItems(item.children, targetSlug)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Checks if a string slug corresponds to the Lesson Plan tab.
 */
export function isLessonTabSlug(slug?: string | null): boolean {
  if (!slug) return false;
  const s = slug.toLowerCase().trim().replace(/^#/, "");
  return s === "lesson" || s === "lesson-plan" || s === "lessonplan" || s === "lesson_plan";
}

/**
 * Checks if a string slug corresponds to the Practice Plan tab.
 */
export function isPracticeTabSlug(slug?: string | null): boolean {
  if (!slug) return false;
  const s = slug.toLowerCase().trim().replace(/^#/, "");
  return s === "practice" || s === "practice-plan" || s === "practiceplan" || s === "practice_plan";
}

